/**
 * The contact lens trial dialog.
 *
 * New wearers and lens changes take a trial pair home before the prescription is issued, and come back
 * to have the fit checked. The lens and its powers are written in the prescription dialog; this records
 * when a trial pair went home, when it is reviewed, and what the review found for each eye - the
 * over-refraction, VA, centration, movement, a toric's rotation and comfort. Every trial is kept with
 * the prescription draft, oldest first: a new pair is another trial, never written over the last, and
 * earlier trials are shown with it. The draft can't be issued until the latest trial is a good fit - the
 * server checks that too.
 *
 * Two steps: trial lenses -> review. The outcome hands back to the prescription dialog at the step it
 * needs - to issue it, to change the powers, or to choose another lens - by closing with
 * { next: "rx", step, prescription }, which the prescriptions tile opens. Only one dialog is open at a
 * time. "Adjust power" carries the review into the prescription: each eye's powers move by its
 * over-refraction and a toric's axis by its rotation, to the box the lens has in them, ready to go home
 * as the next trial.
 */
app.controller("lensRxTrialModalController", [
	"$scope",
	"$mdDialog",
	"$animate",
	"$element",
	"$filter",
	"LensPrescribingStore",
	"LensDispensingStore",
	"CurrentPatient",
	"toast",
	"existingRx",
	"newTrial",
	function ($scope, $mdDialog, $animate, $element, $filter, Rx, Store, CurrentPatient, toast, existingRx, newTrial) {
		$scope.R = Rx;
		$scope.S = Store;

		// See the dispensing dialogs: steps switch instantly and fade themselves in, without ngAnimate.
		$animate.enabled(angular.element($element[0].querySelector(".lens-shell")), false);

		const patient = CurrentPatient.patient || {};
		$scope.patientName = [patient.firstName, patient.lastName].filter(Boolean).join(" ") || "Patient";
		$scope.initials = ((patient.firstName || "P").charAt(0) + (patient.lastName || "").charAt(0)).toUpperCase();

		$scope.steps = [
			{ key: "send", label: "Trial lenses", icon: "fa-paper-plane-o" },
			{ key: "review", label: "Review", icon: "fa-eye" },
		];
		const REVIEW = 1;

		$scope.p = angular.copy(existingRx);
		const p = $scope.p;
		const f = p.fitting;
		$scope.locked = !Rx.isDraft(p);

		const today = () => {
			const d = new Date();
			d.setHours(0, 0, 0, 0);
			return d;
		};
		const date = (d) => $filter("date")(d, "d MMM yyyy");

		// The trial being written: a new pair of what the draft prescribes now - not on the list until it
		// goes home - or the one that's out.
		const isNew = (!!newTrial || !Rx.currentTrial(p)) && !$scope.locked;
		$scope.trial = isNew ? Rx.newTrial(p) : Rx.currentTrial(p);
		const trial = $scope.trial;
		// The trials before this one, newest first, and this one's number.
		$scope.earlier = f.trials.slice(0, isNew ? f.trials.length : f.trials.length - 1).reverse();
		$scope.number = isNew ? f.trials.length + 1 : f.trials.length;
		$scope.sent = () => !isNew || f.trials.indexOf(trial) !== -1;

		// ------------------------------------------------------------------
		// Navigation
		// ------------------------------------------------------------------
		// A trial pair that went home on an earlier day is back for its review.
		$scope.current = $scope.sent() && trial.sent && new Date(trial.sent) < today() ? REVIEW : 0;

		$scope.go = function (i) {
			if (i < 0 || i >= $scope.steps.length || (i === REVIEW && !$scope.sent())) return;
			$scope.current = i;
			const body = $element[0].querySelector(".settings-pane-body");
			if (body) body.scrollTop = 0;
		};

		$scope.stepDone = (key) => (key === "send" ? $scope.sent() : !!trial.outcome);
		$scope.progress = () => Math.round(($scope.steps.filter((s) => $scope.stepDone(s.key)).length / $scope.steps.length) * 100);
		$scope.stepSummary = function (key) {
			if (key === "send") return $scope.sent() ? `Sent home ${date(trial.sent)}` : "Send the trial pair home";
			if (trial.outcome) return Rx.outcomeLabel(trial.outcome);
			return $scope.sent() && trial.review ? `Due ${date(trial.review)}` : "How the trial went";
		};
		// The review is due today or has passed.
		$scope.reviewDue = () => $scope.sent() && !trial.outcome && !!trial.review && new Date(trial.review) <= today();

		let dirty = false;
		$scope.$watch("p", (n, o) => {
			if (n !== o) dirty = true;
		}, true);
		$scope.$watch("trial", (n, o) => {
			if (n !== o) dirty = true;
		}, true);

		// ------------------------------------------------------------------
		// The trial pair, as it went home
		// ------------------------------------------------------------------
		$scope.eyes = [{ key: "od", label: "Right", sub: "OD" }, { key: "os", label: "Left", sub: "OS" }];
		const isNum = (v) => v !== null && v !== undefined && v !== "" && !isNaN(v);
		// The eyes that took a lens home - the same list each digest, for the review table.
		$scope.trialEyes = $scope.eyes.filter((e) => !(trial.rx[e.key] && trial.rx[e.key].noLens));
		$scope.isToric = (k) => !!trial.rx[k] && isNum(trial.rx[k].cyl) && Number(trial.rx[k].cyl) !== 0;

		// One eye of a trial in a line: its lens, then its powers and fit. An older trial may not say.
		$scope.eyeSpec = function (t, k) {
			const rx = t.rx[k];
			if (rx && rx.noLens) return "No lens";
			if (!rx || !isNum(rx.sph)) return "-";
			const lens = t.lens[k] && t.lens[k].name ? `${t.lens[k].name} · ` : "";
			return lens + Store.clRxText(rx);
		};

		// The dates the trial needs: when it went home, and a review on or after that.
		$scope.datesProblem = function () {
			if (!trial.sent) return "The date the trial pair went home is needed.";
			if (!trial.review) return "The review date is needed.";
			return new Date(trial.review) < new Date(trial.sent) ? "The review can't be before the trial pair went home." : null;
		};

		const failMessage = (resp, fallback) => toast.create({ timeout: 6000, message: Store.errorMessage(resp, fallback), className: "alert-danger", dismissible: true });

		$scope.saving = false;
		function save() {
			Store.syncTrial(f);
			$scope.saving = true;
			return Rx.save(patient.id, p)
				.catch(function (resp) {
					failMessage(resp, "The trial could not be saved.");
					throw resp;
				})
				.finally(() => ($scope.saving = false));
		}

		// The pair goes home: the trial joins the draft's list.
		$scope.sendHome = function () {
			if ($scope.locked || $scope.saving || $scope.datesProblem() || $scope.sent()) return;
			f.trials.push(trial);
			save().then((saved) => $mdDialog.hide(saved), () => f.trials.pop());
		};

		// A trial sent home by mistake is taken off the list; any before it stay.
		$scope.removeTrial = function () {
			if ($scope.locked || $scope.saving || !$scope.sent()) return;
			f.trials.pop();
			save().then((saved) => $mdDialog.hide(saved), angular.noop);
		};

		// ------------------------------------------------------------------
		// The review
		// ------------------------------------------------------------------
		$scope.outcomes = Rx.TRIAL_OUTCOMES;
		$scope.fit = Rx.TRIAL_FIT;
		$scope.setOutcome = function (key) {
			if (!$scope.locked) trial.outcome = key;
		};
		$scope.setFinding = function (k, field, value) {
			if ($scope.locked) return;
			const findings = trial.findings[k];
			findings[field] = findings[field] === value ? null : value;
		};

		// A residual cylinder through a spherical lens is recorded, not added: worth a toric.
		$scope.residualCyl = () => $scope.trialEyes.some((e) => !$scope.isToric(e.key) && Math.abs(Number(trial.findings[e.key].orCyl) || 0) >= 0.75);

		// One eye's review in a line, for an earlier trial.
		$scope.findingsText = function (t, k) {
			const r = t.findings[k] || {};
			const parts = [];
			if (isNum(r.orSph) || isNum(r.orCyl)) parts.push(`OR ${Store.fmtPower(r.orSph || 0)}${Number(r.orCyl) ? ` / ${Store.fmtPower(r.orCyl)} × ${r.orAxis || "?"}` : ""}`);
			if (r.va) parts.push(`VA ${r.va}`);
			["centration", "movement", "comfort"].forEach((kind) => r[kind] && parts.push(`${kind === "comfort" ? "comfort " : ""}${Rx.fitLabel(kind, r[kind]).toLowerCase()}`));
			if (Number(r.rotation) > 0) parts.push(`rotates ${r.rotation}° ${r.rotationDir || ""}`.trim());
			return parts.join(" · ");
		};

		// What the outcome leads to in the prescription dialog, and the step it opens at.
		const NEXT = {
			good: { step: "issue", label: "Prescribe", hint: "The prescription can be issued now - it opens at Review & issue." },
			adjust: { step: "fitting", label: "Change the powers", hint: "The powers move by the over-refraction, and a toric's axis by its rotation - then send the new pair home on trial." },
			refit: { step: "lens", label: "Choose another lens", hint: "Choose another lens in the prescription, then send it home on trial." },
		};
		$scope.nextFor = () => NEXT[trial.outcome] || null;

		// The powers the review points to, for "Adjust power": on the prescription for each eye that went
		// home, in the box the lens has in them - or, where it has none, the powers it needs, for the
		// prescription to say which box to add in inventory.
		$scope.adjusted = function (k) {
			const lens = Store.lensFor(p, k);
			return Rx.adjustedRx(trial, k, !!(lens && lens.custom));
		};
		function applyAdjustment() {
			["od", "os"].forEach(function (k) {
				const a = $scope.adjusted(k);
				const lens = Store.lensFor(p, k);
				if (!a || !lens || p.cl[k].noLens) return;
				if (lens.boxes) {
					const box = Store.boxFor(lens, angular.extend({}, a, { bc: trial.rx[k].bc }));
					angular.extend(p.cl[k], box ? box.rx : angular.extend(a, { bc: null, dia: null }));
				} else {
					angular.extend(p.cl[k], a);
				}
			});
			p.cl.source = `Adjusted after trial ${$scope.number}`;
		}

		$scope.toPrescription = function () {
			const next = $scope.nextFor();
			if (!next || $scope.locked || $scope.saving) return;
			if (trial.outcome === "adjust") applyAdjustment();
			save().then((saved) => $mdDialog.hide({ next: "rx", step: next.step, prescription: saved }), angular.noop);
		};

		// Back to the prescription to change the lens or powers. A new trial pair not yet sent is left as it
		// was; one that is out keeps what was written here.
		$scope.changeLens = function () {
			if ($scope.locked || $scope.saving) return;
			if (!$scope.sent()) return $mdDialog.hide({ next: "rx", step: "fitting", prescription: existingRx });
			save().then((saved) => $mdDialog.hide({ next: "rx", step: "fitting", prescription: saved }), angular.noop);
		};

		$scope.saveAndClose = function () {
			if ($scope.locked || $scope.saving) return;
			save().then((saved) => $mdDialog.hide(saved), angular.noop);
		};

		// ------------------------------------------------------------------
		// Close
		// ------------------------------------------------------------------
		// A new trial pair not yet sent has nothing to keep; a trial that is out asks before losing changes.
		$scope.confirmClose = false;
		$scope.keepEditing = () => ($scope.confirmClose = false);
		$scope.close = function () {
			if (dirty && $scope.sent() && !$scope.locked) {
				$scope.confirmClose = true;
				return;
			}
			$mdDialog.cancel();
		};
		$scope.discard = () => $mdDialog.cancel();
	},
]);
